import React from 'react';
import { PromptModal, ConfirmModal } from './ui/Modal';
import { ToastContainer } from './ui/Toast';
import NotificationContainer from './ui/NotificationContainer';
import SignOutLocalDataDialog from './auth/SignOutLocalDataDialog';
import ImportCollectionModal from './ImportCollectionModal';
import ExportCollectionModal from './ExportCollectionModal';
import CollectionRunnerModal from './CollectionRunnerModal';
import CodeGeneratorModal from './CodeGeneratorModal';
import WorkspaceModal from './WorkspaceModal';
import ShareLinkModal from './ShareLinkModal';
import TeamsModal from './TeamsModal';
import CollectionSelectionModal from './CollectionSelectionModal';
import PublishLocalWorkspaceModal from './PublishLocalWorkspaceModal';
import GitConnectModal from './GitConnectModal';
import { isLocalOnly } from '../services/git/gitSyncStatus';
import GitConflictModal from './GitConflictModal';
import GitSecretsModal from './GitSecretsModal';
import ForkWorkspaceModal from './ForkWorkspaceModal';
import { useShallow } from 'zustand/react/shallow';
import CommandPalette from './palette/CommandPalette';
import { closePalette } from './palette/paletteOpenState';
import { openHelp } from '../help/helpStore';
import { rebuildPaletteIndex } from '../palette/indexMaintenance';
import AuthModal from './auth/AuthModal';
import SettingsModal from './SettingsModal';
import EnvironmentsModal from './EnvironmentsModal';
import PlanUpgradeModal from './PlanUpgradeModal';
import VaultCapPrompt from './VaultCapPrompt';
import UpdateModal from './UpdateModal';
import ShareAcceptModal from './ShareAcceptModal';
import ShareToChatModal from './ShareToChatModal';
import ImportNetworkModal from './ImportNetworkModal';
import PublishToNetworkModal from './PublishToNetworkModal';
import { useDevToolsStore } from '../store/devToolsStore';
import { isBackendWorkspace } from '../utils/workspaceStorage';
import { folderVariablesForRequest } from '../utils/folderVariables';
import { scopeRowsLowestFirst } from '../utils/variablePrecedence';
import {
  useUiModalStore,
  setForkModalWorkspace, setGitConnectWorkspace, setGitConflict, setGitSecrets,
  setPublishModalWorkspace, setPublishNetworkWorkspace, setShowWorkspaceModal,
  setSelectedWorkspaceForEdit, setShowTeamsModal, setTeamsModalMode,
  setSelectedTeam, setAuthModalOpen, setSettingsModalOpen,
  setUpgradeModalOpen, setUpgradeReason, setSettingsInitialTab, setShareItem, setShareAcceptToken,
  setShareToChatTarget, setActiveTab,
  setNetworkImportSlug, setJoinDeepLinkCode, setResetDeepLink,
  setShowCollectionModal, setShowImportCollectionModal, setShowExportCollectionModal,
  setExportCollectionTarget, setShowCodeGenModal, setCodeGenTarget,
  setShowClearHistoryModal, setShowCollectionSelectionModal,
  setShowExportCollectionPickerModal, setPendingRequestData, setPendingRequestTabId,
  setShowEnvironmentsModal, setAiPanelOpen,
} from '../store/uiModalStore';

// The command palette's own flags: CommandPalette subscribes to them itself, so
// opening it does not re-render every modal here (palette PLAN §3.3).
const PALETTE_KEYS = new Set(['showCommandPalette', 'commandPaletteFilter', 'commandPaletteMode']);
function modalStackState(s) {
  const out = {};
  for (const k of Object.keys(s)) if (typeof s[k] !== 'function' && !PALETTE_KEYS.has(k)) out[k] = s[k];
  return out;
}

/**
 * Aggregates every top-level modal/overlay. Modal visibility + payloads are read
 * from uiModalStore (and its stable setter functions); behavior handlers and the
 * domain data the modals need are received as props from App.
 */
export default function ModalStack({
  // domain data
  currentWorkspace,
  isAuthenticated,
  user,
  teams,
  workspaces,
  collections,
  requestTabs,
  collectionTabs,
  schemaTabs,
  environments,
  selectedEnvironment,
  workspaceVariables,
  globalVariables,
  collectionVariablesById,
  gitAccount,
  gitSyncingWorkspaceId,
  publishingWorkspaceId,
  forkingWorkspaceId,
  // update state (owned by App until updateStore lands)
  updateInfo,
  updateModalOpen,
  updateInstalling,
  updateError,
  onUpdateModalClose,
  handleInstallUpdate,
  handleUpdateLater,
  handleSkipVersion,
  // toast
  showToast,
  toasts,
  removeToast,
  // collection / request handlers
  handleCreateCollection,
  createFolderReturningId,
  handleImportCollections,
  handleImportEnvironment,
  handleCollectionRunnerClose,
  handleCollectionRunnerComplete,
  handleClearHistory,
  getExportableCollections,
  handleSelectCollectionForPendingRequest,
  handleSelectCollectionForExport,
  createCollection,
  openImportCollection,
  openExportCollectionFromPalette,
  refreshCollections,
  reloadWorkspacesFromServer,
  closeAllTabs,
  selectRequest,
  openCollectionTab,
  handleTabChange,
  handleNewRequestTab,
  // workspace / git handlers
  handleWorkspaceCreated,
  handleWorkspaceUpdated,
  handleWorkspaceCloned,
  handlePublishLocalConfirm,
  handleGitConnectConfirm,
  handleGitResolveConflicts,
  handleForkWorkspaceConfirm,
  switchWorkspace,
  // team handlers
  handleTeamCreated,
  handleTeamUpdated,
  handleTeamJoined,
  handleTeamsModal,
  handleOpenTeamFromCommandPalette,
  handleShareWithTeamSuccess,
  // auth / settings / variables / network handlers
  openAuthModal,
  handleSignOut,
  handleManualUpdateCheck,
  setEnvironments,
  setSelectedEnvironment,
  saveVariableScope,
  handleVariableUpdate,
  handleNetworkImported,
}) {
  const {
    showCollectionModal,
    showImportCollectionModal,
    importCollectionLoading,
    showExportCollectionModal,
    exportCollectionTarget,
    showCollectionRunnerModal,
    collectionRunnerTarget,
    collectionRunnerFolderId,
    collectionRunnerAutoRun,
    showCodeGenModal,
    codeGenTarget,
    showClearHistoryModal,
    showWorkspaceModal,
    workspaceModalMode,
    selectedWorkspaceForEdit,
    shareItem,
    showTeamsModal,
    teamsModalMode,
    selectedTeam,
    joinDeepLinkCode,
    showCollectionSelectionModal,
    showExportCollectionPickerModal,
    pendingRequestData,
    publishModalWorkspace,
    gitConnectWorkspace,
    gitConflict,
    gitSecrets,
    forkModalWorkspace,
    authModalOpen,
    authModalMode,
    resetDeepLink,
    settingsModalOpen,
    settingsInitialTab,
    showEnvironmentsModal,
    upgradeModalOpen,
    upgradeReason,
    planModalOpen,
    shareAcceptToken,
    shareToChatTarget,
    networkImportSlug,
    publishNetworkWorkspace,
  } = useUiModalStore(useShallow(modalStackState));

  return (
    <>
      <PromptModal
        isOpen={showCollectionModal}
        onClose={() => setShowCollectionModal(false)}
        onConfirm={handleCreateCollection}
        title="Create Collection"
        message="Enter a name for your new collection"
        placeholder="Collection name"
        inputClassName="modal-input-flat"
        hideCancel
      />

      <ImportCollectionModal
        isOpen={showImportCollectionModal}
        onClose={() => !importCollectionLoading && setShowImportCollectionModal(false)}
        onImport={handleImportCollections}
        onImportEnvironment={handleImportEnvironment}
        importing={importCollectionLoading}
      />

      <ExportCollectionModal
        isOpen={showExportCollectionModal}
        onClose={() => {
          setShowExportCollectionModal(false);
          setExportCollectionTarget(null);
        }}
        collection={exportCollectionTarget}
        loadFullFromApi={isBackendWorkspace(currentWorkspace, isAuthenticated)}
        showToast={showToast}
      />

      <CollectionRunnerModal
        isOpen={showCollectionRunnerModal}
        onClose={handleCollectionRunnerClose}
        collection={collectionRunnerTarget}
        folderId={collectionRunnerFolderId}
        autoRun={collectionRunnerAutoRun}
        onRunComplete={handleCollectionRunnerComplete}
        selectedEnvironment={selectedEnvironment}
        collectionVariables={collectionVariablesById[collectionRunnerTarget?.id] || []}
        workspaceVariables={workspaceVariables}
        globalVariables={globalVariables}
      />

      <CodeGeneratorModal
        isOpen={showCodeGenModal}
        onClose={() => {
          setShowCodeGenModal(false);
          setCodeGenTarget(null);
        }}
        request={codeGenTarget?.request}
        collectionAuth={codeGenTarget?.collectionAuth}
        folderAuthChain={codeGenTarget?.folderAuthChain}
        // Order is precedence, LOWEST first: buildRedactingSubstitute fills a
        // Map in sequence, so the last entry for a key wins — built from THE
        // order (utils/variablePrecedence), nearest folder last.
        variables={scopeRowsLowestFirst({
          global: globalVariables,
          workspace: workspaceVariables,
          environment: selectedEnvironment?.variables,
          collection: collectionVariablesById?.[codeGenTarget?.collectionId],
          folder: folderVariablesForRequest(codeGenTarget?.request, collections, codeGenTarget?.collectionId),
        })}
      />

      <ConfirmModal
        isOpen={showClearHistoryModal}
        onClose={() => setShowClearHistoryModal(false)}
        onConfirm={handleClearHistory}
        title="Clear History"
        message="Are you sure you want to clear all request history? This action cannot be undone."
        confirmText="Clear History"
        cancelText="Cancel"
        type="danger"
      />

      <WorkspaceModal
        isOpen={showWorkspaceModal}
        onClose={() => {
          setShowWorkspaceModal(false);
          setSelectedWorkspaceForEdit(null);
        }}
        onWorkspaceCreated={handleWorkspaceCreated}
        onWorkspaceUpdated={handleWorkspaceUpdated}
        onWorkspaceCloned={handleWorkspaceCloned}
        mode={workspaceModalMode}
        workspace={selectedWorkspaceForEdit}
      />

      <ShareLinkModal
        isOpen={!!shareItem}
        onClose={() => setShareItem(null)}
        type={shareItem?.type ?? 'collection'}
        item={shareItem?.item ?? null}
        showToast={showToast}
        teams={teams}
        workspaces={workspaces}
        currentUserId={user?.id}
        onTeamShareSuccess={handleShareWithTeamSuccess}
        initialTab={shareItem?.initialTab ?? 'shareable'}
        fixedTeam={shareItem?.fixedTeam ?? null}
      />

      <TeamsModal
        isOpen={showTeamsModal}
        onClose={() => {
          setShowTeamsModal(false);
          setSelectedTeam(null);
          setJoinDeepLinkCode(null);
        }}
        mode={teamsModalMode}
        team={selectedTeam}
        defaultInviteCode={joinDeepLinkCode}
        onTeamCreated={handleTeamCreated}
        onTeamUpdated={handleTeamUpdated}
        onTeamJoined={(team) => { setJoinDeepLinkCode(null); handleTeamJoined(team); }}
        onModeSwitch={(mode) => {
          setTeamsModalMode(mode);
          setShowTeamsModal(true);
        }}
        onUpgradeRequired={(reason) => {
          setUpgradeReason(reason);
          setUpgradeModalOpen(true);
        }}
      />

      <CollectionSelectionModal
        isOpen={showCollectionSelectionModal}
        onClose={() => {
          setShowCollectionSelectionModal(false);
          setPendingRequestData(null);
          setPendingRequestTabId(null);
        }}
        collections={getExportableCollections()}
        onSelectCollection={handleSelectCollectionForPendingRequest}
        allowFolders
        allowCreate
        showNameInput
        initialName={pendingRequestData?.name || ''}
        onCreateCollection={handleCreateCollection}
        onCreateFolder={createFolderReturningId}
      />

      <CollectionSelectionModal
        isOpen={showExportCollectionPickerModal}
        onClose={() => setShowExportCollectionPickerModal(false)}
        collections={getExportableCollections()}
        title="Export collection"
        description="Choose a collection to export:"
        confirmLabel="Export"
        onSelectCollection={handleSelectCollectionForExport}
      />

      <PublishLocalWorkspaceModal
        isOpen={Boolean(publishModalWorkspace)}
        onClose={() => setPublishModalWorkspace(null)}
        workspace={publishModalWorkspace}
        loading={Boolean(
          publishModalWorkspace && publishingWorkspaceId === publishModalWorkspace.id
        )}
        onConfirm={handlePublishLocalConfirm}
      />

      <GitConnectModal
        isOpen={Boolean(gitConnectWorkspace)}
        onClose={() => setGitConnectWorkspace(null)}
        workspace={gitConnectWorkspace}
        mode={isLocalOnly(gitConnectWorkspace) ? 'addRemote' : 'connect'}
        allowLocal
        onConnect={handleGitConnectConfirm}
        onOpenGitPage={() => {
          setGitConnectWorkspace(null);
          setActiveTab('git');
        }}
        loading={Boolean(gitConnectWorkspace && gitSyncingWorkspaceId === gitConnectWorkspace.id)}
      />

      <GitConflictModal
        isOpen={Boolean(gitConflict)}
        onClose={() => setGitConflict(null)}
        conflicts={gitConflict?.conflicts || []}
        onResolve={handleGitResolveConflicts}
        loading={Boolean(gitConflict && gitSyncingWorkspaceId === gitConflict.workspace?.id)}
      />

      <GitSecretsModal
        isOpen={Boolean(gitSecrets)}
        onClose={() => setGitSecrets(null)}
        findings={gitSecrets?.findings || []}
        onCommitAnyway={() => {
          const retry = gitSecrets?.commitAnyway;
          setGitSecrets(null);
          retry?.();
        }}
        loading={Boolean(gitSecrets && gitSyncingWorkspaceId === gitSecrets.workspace?.id)}
      />

      <ForkWorkspaceModal
        isOpen={Boolean(forkModalWorkspace)}
        onClose={() => setForkModalWorkspace(null)}
        sourceWorkspace={forkModalWorkspace}
        localWorkspaces={workspaces.filter((w) => w.storage === 'local')}
        isAuthenticated={isAuthenticated}
        isOnline={typeof navigator !== 'undefined' ? navigator.onLine : true}
        loading={Boolean(forkingWorkspaceId)}
        onConfirm={handleForkWorkspaceConfirm}
      />

      <CommandPalette
        nav={{
          onSwitchWorkspace: switchWorkspace,
          onSelectRequest: selectRequest,
          onOpenCollection: openCollectionTab,
          onTabChange: handleTabChange,
          onNewTab: handleNewRequestTab,
          onCreateCollection: createCollection,
          onImportCollection: openImportCollection,
          onExportCollection: openExportCollectionFromPalette,
          onRefreshCollections: refreshCollections,
          onCloseAllTabs: closeAllTabs,
          onCreateTeam: () => handleTeamsModal('create'),
          onJoinTeam: () => handleTeamsModal('join'),
          onOpenTeam: handleOpenTeamFromCommandPalette,
          onOpenAuth: openAuthModal,
          onSignOut: handleSignOut,
          onOpenSettings: (tab) => { if (tab) setSettingsInitialTab(tab); setSettingsModalOpen(true); },
          onOpenHelp: () => openHelp(),
          onToggleAiPanel: () => setAiPanelOpen((open) => !open),
          onToggleDevTools: () => useDevToolsStore.getState().toggle(),
          rebuildSearchIndex: rebuildPaletteIndex,
          close: () => closePalette(),
        }}
      />

      <AuthModal
        isOpen={authModalOpen}
        initialMode={authModalMode}
        resetLink={resetDeepLink}
        onResetComplete={() => setResetDeepLink(null)}
        onClose={() => {
          setResetDeepLink(null);
          setAuthModalOpen(false);
        }}
        showToast={showToast}
      />

      <SettingsModal
        isOpen={settingsModalOpen}
        onClose={() => { setSettingsModalOpen(false); setSettingsInitialTab(null); }}
        onCheckForUpdates={handleManualUpdateCheck}
        initialTab={settingsInitialTab}
        showToast={showToast}
      />

      <EnvironmentsModal
        isOpen={showEnvironmentsModal}
        onClose={() => setShowEnvironmentsModal(false)}
        environments={environments}
        selectedEnvironment={selectedEnvironment}
        globalVariables={globalVariables}
        workspaceVariables={workspaceVariables}
        onEnvironmentsChange={setEnvironments}
        onSelectedEnvironmentChange={setSelectedEnvironment}
        onSaveScope={saveVariableScope}
        currentWorkspaceId={currentWorkspace?.id ?? null}
        onVariableUpdate={handleVariableUpdate}
      />

      {/* Opened when the SERVER refused something for want of a bigger plan —
          a workspace, a collection, a team, an AI call. `forceUpgrade` is not
          optional here: without it PlanUpgradeModal computes
          `showManage = isPaid && !forceUpgrade` and a Pro user who just hit the
          team limit is shown "Manage your plan" — cancel, resume, invoices —
          with no way to see the plan that would actually let them do the thing
          they tried to do. The one moment someone is most ready to upgrade was
          the one moment the product hid the upgrade. */}
      <PlanUpgradeModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        reason={upgradeReason}
        showToast={showToast}
        forceUpgrade
      />

      <VaultCapPrompt />

      <PlanUpgradeModal
        isOpen={planModalOpen}
        onClose={() => useUiModalStore.getState().setPlanModalOpen(false)}
        showToast={showToast}
        forceUpgrade
      />

      <UpdateModal
        isOpen={updateModalOpen}
        onClose={onUpdateModalClose}
        currentVersion={updateInfo?.currentVersion || ''}
        latest={updateInfo?.latest}
        offeredVersion={updateInfo?.artifactVersion || updateInfo?.latest?.version || ''}
        onInstall={handleInstallUpdate}
        onLater={handleUpdateLater}
        onSkipVersion={handleSkipVersion}
        installing={updateInstalling}
        error={updateError}
      />

      <ShareAcceptModal
        isOpen={!!shareAcceptToken}
        onClose={() => setShareAcceptToken(null)}
        token={
          typeof shareAcceptToken === 'string' ? shareAcceptToken : shareAcceptToken?.token || null
        }
        channelId={typeof shareAcceptToken === 'string' ? null : shareAcceptToken?.channelId ?? null}
        encHint={typeof shareAcceptToken === 'string' ? null : shareAcceptToken?.enc ?? null}
        workspaces={workspaces}
        teams={teams}
        isAuthenticated={isAuthenticated}
        showToast={showToast}
        onWorkspaceAdded={async () => {
          // Accepting can create a brand-new workspace ("New workspace…" or a
          // workspace fork) — the workspace list must reload or it never shows.
          await reloadWorkspacesFromServer?.();
          refreshCollections?.();
        }}
      />

      <ShareToChatModal
        isOpen={!!shareToChatTarget}
        onClose={() => setShareToChatTarget(null)}
        target={shareToChatTarget}
        showToast={showToast}
      />

      <ImportNetworkModal
        isOpen={!!networkImportSlug}
        onClose={() => setNetworkImportSlug(null)}
        slug={networkImportSlug}
        showToast={showToast}
        onImported={handleNetworkImported}
      />

      <PublishToNetworkModal
        isOpen={Boolean(publishNetworkWorkspace)}
        onClose={() => setPublishNetworkWorkspace(null)}
        workspace={publishNetworkWorkspace}
        showToast={showToast}
        onUpgradeRequired={(reason) => {
          setPublishNetworkWorkspace(null);
          setUpgradeReason(reason || 'Publishing to the API Network requires a Team or Enterprise plan.');
          setUpgradeModalOpen(true);
        }}
      />

      <ToastContainer toasts={toasts} onClose={removeToast} />
      <NotificationContainer />
      <SignOutLocalDataDialog />
    </>
  );
}

